import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  Modal,
  Button,
  Checkbox,
  Radio,
  Input,
  Space,
  Spin,
  Typography,
  Divider,
  Segmented,
  message,
  Empty
} from 'antd'
import { ClearOutlined, EditOutlined, SendOutlined } from '@ant-design/icons'
import styled from 'styled-components'
import { getApiErrorMessage } from '../utils/api'
import {
  listPurchaseApplicableForms,
  signConsentInline,
  sendPurchaseConsent
} from '../api/consentForm'
import BrandHint from './common/Hint'

const { Text, Paragraph } = Typography

const ContentBox = styled.div`
  white-space: pre-wrap;
  line-height: 1.8;
  color: #333;
  background: #fafafa;
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 14px;
  max-height: 260px;
  overflow-y: auto;
`

/* 簽名整區禁止文字選取與長按選單，避免簽名拖曳被當成「選取文字」反白 */
const SignSection = styled.div`
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;
`

const CanvasWrap = styled.div`
  border: 1px dashed #5b8def;
  border-radius: 8px;
  background: #fff;
  position: relative;
  touch-action: none;
  overscroll-behavior: contain;
`

const StyledCanvas = styled.canvas`
  width: 100%;
  height: clamp(180px, 26vh, 280px);
  display: block;
  border-radius: 8px;
  touch-action: none;
`

const Hint = styled.div`
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  text-align: center;
  transform: translateY(-50%);
  color: #ccc;
  pointer-events: none;
`

// 客戶端變數代入（僅供顯示；正式 snapshot 由後端渲染）
function render(content, variables = {}) {
  if (!content) return ''
  return String(content).replace(/\{\{\s*([\w.]+)\s*\}\}/g, (m, k) =>
    variables[k] === undefined || variables[k] === null ? m : String(variables[k])
  )
}

/**
 * 購買同意書簽署 Modal（堂票發放 / 儲值共用）。
 * props:
 *  - open, onClose
 *  - businessId, customerId, customerName
 *  - target: 'classTicket' | 'balance'
 *  - variables: { planName, expiryDate, amount, price, customerName }
 *  - onSigned(signatureId): 現場簽署完成，回傳 signatureId 供購買帶入
 *  - onSentRemote(): 已傳送給客人（遠端簽）
 *  - onNoForm(): 此購買無綁定購買同意書（呼叫端可直接完成購買）
 */
const PurchaseConsentModal = ({
  open,
  onClose,
  businessId,
  customerId,
  customerName,
  target,
  // 正在購買的堂票範本 ID；同意書有限定堂票時，只撈適用這種堂票的
  classTicketTemplateId = null,
  variables = {},
  subjectRef = null, // 購買完成後可帶入（如訂單 / 票券 id）以綁定簽署
  reminderMode = false, // true＝購買已完成的提醒（不顯示「略過並完成」）
  autoSent = false, // true＝後端已自動發送給顧客（隱藏「傳送給客人」避免重複發送，僅保留現場簽）
  onSigned,
  onSentRemote,
  onNoForm,
  onSkip
}) => {
  const [loading, setLoading] = useState(false)
  const [templates, setTemplates] = useState([])
  /**
   * 適用範本可能不只一份（例如一份「課程堂數使用同意書」＋一份「計次券使用說明」，
   * 兩份都沒限定堂票時，任何一張票都同時適用）。
   *
   * 原本這裡是 templates[0] —— 第二份以後從發放流程裡完全看不到、也選不到，
   * 等於店家設了卻永遠不會被簽。現在改成一份一份走完：簽（或傳送）完一份就
   * 自動跳下一份，全部處理完才回呼叫端。
   */
  const [activeIdx, setActiveIdx] = useState(0)
  // { [templateId]: 'signed' | 'sent' } —— 這次視窗裡已經處理掉的
  const [doneMap, setDoneMap] = useState({})
  // 第一份現場簽的 signatureId：呼叫端拿它綁定這筆購買，維持原本的契約
  const firstSignatureId = useRef(null)
  const [mode, setMode] = useState('onsite') // onsite | remote
  const [checked, setChecked] = useState({})
  // 問卷作答：{ [題目 key]: { selectedKeys: [], textValue: '' } }
  const [answers, setAnswers] = useState({})
  const [signerName, setSignerName] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const canvasRef = useRef(null)
  const drawing = useRef(false)
  const hasInk = useRef(false)
  const [showHint, setShowHint] = useState(true)

  const template = templates[activeIdx] || null
  const pendingTemplates = templates.filter((t) => !doneMap[t._id])

  /**
   * onNoForm 會讓呼叫端「直接完成購買」，所以每次打開視窗最多只能回呼一次。
   *
   * 以前 onNoForm 放在 load 的 deps 裡，而呼叫端都是傳 inline 函式（每次 render
   * 都是新的）：視窗開著、清單還在載入時父層只要 render 一次，就會多發一次 load；
   * 店家沒綁同意書時兩次都回傳空清單，onNoForm 就被呼叫兩次 —— 等於直接儲值兩次，
   * 而且第二次用的是舊的 closure，表單清空了還是會送出同樣的金額。
   * （2026-09-13 修修甲鍾荻穎重複儲值 11,000 就是這個情境。）
   *
   * 改成：onNoForm 用 ref 保存（不影響 load 的身分）；load 帶序號，只認最新一次，
   * 且結果回來時視窗必須還開著；另以旗標保證每次打開只回呼一次。
   */
  const onNoFormRef = useRef(onNoForm)
  onNoFormRef.current = onNoForm
  const openRef = useRef(open)
  openRef.current = open
  const loadSeqRef = useRef(0)
  const noFormFiredRef = useRef(false)

  const load = useCallback(async () => {
    if (!businessId || !target) return
    const seq = ++loadSeqRef.current
    setLoading(true)
    try {
      const res = await listPurchaseApplicableForms(
        businessId,
        target,
        classTicketTemplateId
      )
      // 期間又發了新的 load，或視窗已經關掉：這次的結果作廢
      if (seq !== loadSeqRef.current || !openRef.current) return
      const list = res?.data?.data || []
      setTemplates(list)
      if (list.length === 0 && !noFormFiredRef.current) {
        noFormFiredRef.current = true
        onNoFormRef.current && onNoFormRef.current()
      }
    } catch (err) {
      if (seq !== loadSeqRef.current || !openRef.current) return
      message.error(getApiErrorMessage(err, '取得購買同意書失敗'))
    } finally {
      if (seq === loadSeqRef.current) setLoading(false)
    }
  }, [businessId, target, classTicketTemplateId])

  useEffect(() => {
    if (!open) {
      // 關閉時讓進行中的 load 作廢；下次打開重新允許回呼一次 onNoForm
      loadSeqRef.current += 1
      noFormFiredRef.current = false
      return
    }
    setChecked({})
    setAnswers({})
    setActiveIdx(0)
    setDoneMap({})
    firstSignatureId.current = null
    setSignerName(customerName || variables.customerName || '')
    setMode('onsite') // autoSent 時固定現場簽（遠端已由系統自動發送）
    hasInk.current = false
    setShowHint(true)
    load()
  }, [open, load, customerName, variables.customerName])

  // 畫布尺寸
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas || !open || mode !== 'onsite' || !template) return
    const rect = canvas.getBoundingClientRect()
    canvas.width = rect.width
    canvas.height = rect.height
  }, [open, mode, template])

  const getPos = (e) => {
    const canvas = canvasRef.current
    const rect = canvas.getBoundingClientRect()
    const cx = e.touches ? e.touches[0].clientX : e.clientX
    const cy = e.touches ? e.touches[0].clientY : e.clientY
    return {
      x: (cx - rect.left) * (canvas.width / rect.width),
      y: (cy - rect.top) * (canvas.height / rect.height)
    }
  }
  // React 對 touch 事件註冊為 passive，onTouchMove 內 preventDefault 無效，
  // 需以原生 non-passive 監聽器攔截，才能真正阻止捲動與文字選取
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas || !open || mode !== 'onsite' || !template) return
    const prevent = (ev) => ev.preventDefault()
    canvas.addEventListener('touchstart', prevent, { passive: false })
    canvas.addEventListener('touchmove', prevent, { passive: false })
    return () => {
      canvas.removeEventListener('touchstart', prevent)
      canvas.removeEventListener('touchmove', prevent)
    }
  }, [open, mode, template])

  const startDraw = (e) => {
    e.preventDefault?.()
    drawing.current = true
    // 簽名期間全頁禁止文字選取（手指滑出畫布也不反白）
    document.body.style.userSelect = 'none'
    document.body.style.webkitUserSelect = 'none'
    const ctx = canvasRef.current.getContext('2d')
    const { x, y } = getPos(e)
    ctx.beginPath()
    ctx.moveTo(x, y)
  }
  const moveDraw = (e) => {
    if (!drawing.current) return
    e.preventDefault()
    const ctx = canvasRef.current.getContext('2d')
    ctx.lineWidth = 2.5
    ctx.lineCap = 'round'
    ctx.strokeStyle = '#222'
    const { x, y } = getPos(e)
    ctx.lineTo(x, y)
    ctx.stroke()
    hasInk.current = true
    if (showHint) setShowHint(false)
  }
  const endDraw = () => {
    drawing.current = false
    document.body.style.userSelect = ''
    document.body.style.webkitUserSelect = ''
    try {
      window.getSelection?.()?.removeAllRanges?.()
    } catch (_) {
      /* ignore */
    }
  }
  const clearCanvas = () => {
    const canvas = canvasRef.current
    if (!canvas) return
    canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height)
    hasInk.current = false
    setShowHint(true)
  }

  const validateChecks = () => {
    for (const item of template?.checkboxes || []) {
      if (item.required && !checked[item.key]) {
        message.warning(`請勾選：${item.label}`)
        return false
      }
    }
    return true
  }

  const buildCheckedItems = () =>
    (template?.checkboxes || []).map((c) => ({
      key: c.key,
      checked: !!checked[c.key]
    }))

  const validateAnswers = () => {
    for (const q of template?.questions || []) {
      if (!q.required) continue
      const a = answers[q.key] || {}
      if (q.type === 'text') {
        if (!String(a.textValue || '').trim()) {
          message.warning(`請填寫：${q.label}`)
          return false
        }
      } else if (!(a.selectedKeys || []).length) {
        message.warning(`請回答：${q.label}`)
        return false
      }
    }
    return true
  }

  const buildAnswers = () =>
    (template?.questions || []).map((q) => ({
      key: q.key,
      selectedKeys: answers[q.key]?.selectedKeys || [],
      textValue: answers[q.key]?.textValue || ''
    }))

  /**
   * 處理完一份之後往下走。
   * 還有沒處理的就切過去並清空這一份的作答／簽名；全部處理完才回呼叫端
   * （呼叫端會用回傳的 signatureId 綁定這筆購買，所以只回第一份的）。
   */
  const advanceOrFinish = (templateId, how, signatureId) => {
    const nextDone = { ...doneMap, [templateId]: how }
    setDoneMap(nextDone)
    if (how === 'signed' && !firstSignatureId.current && signatureId) {
      firstSignatureId.current = signatureId
    }
    const nextIdx = templates.findIndex((t) => !nextDone[t._id])
    if (nextIdx >= 0) {
      setActiveIdx(nextIdx)
      setChecked({})
      setAnswers({})
      clearCanvas()
      message.info(
        `還有 ${templates.length - Object.keys(nextDone).length} 份同意書要處理`
      )
      return
    }
    if (firstSignatureId.current) {
      onSigned && onSigned(firstSignatureId.current)
    } else {
      onSentRemote && onSentRemote()
    }
  }

  /**
   * 略過（稍後再簽）：把還沒處理的每一份都留一筆待簽紀錄再走。
   *
   * 這是這個按鈕原本最大的問題 —— 它以前只是把視窗關掉，什麼都不留，
   * 所以事後在系統裡完全找不到「稍後」要簽的是什麼。notify: false 代表
   * 只建紀錄、不推播給客人（店家按的是略過，不是傳送）。
   * 後端對同一份、同一筆購買會沿用既有的待簽紀錄，重複按不會長出一堆。
   */
  const handleSkip = async () => {
    const rest = pendingTemplates
    if (rest.length && customerId) {
      setSubmitting(true)
      try {
        for (const t of rest) {
          await sendPurchaseConsent(businessId, {
            templateId: t._id,
            target,
            customerId,
            subjectRef: subjectRef || undefined,
            variables,
            notify: false
          })
        }
        message.success(`已留下 ${rest.length} 份待簽紀錄，可在同意書紀錄補簽或補發`)
      } catch (err) {
        // 留紀錄失敗不該把購買卡住，但要讓店家知道「稍後」沒有留下來
        message.warning(
          getApiErrorMessage(err, '待簽紀錄建立失敗，這份同意書不會出現在紀錄裡')
        )
      } finally {
        setSubmitting(false)
      }
    }
    if (onSkip) onSkip()
    else onClose && onClose()
  }

  const handleSignOnsite = async () => {
    if (!template) return
    if (!validateChecks()) return
    if (!validateAnswers()) return
    if (!signerName.trim()) {
      message.warning('請填寫簽署人姓名')
      return
    }
    let signatureImage = ''
    if (template.requireSignature) {
      if (!hasInk.current) {
        message.warning('請完成手寫簽名')
        return
      }
      signatureImage = canvasRef.current.toDataURL('image/png')
    }
    setSubmitting(true)
    try {
      const res = await signConsentInline(businessId, {
        templateId: template._id,
        target,
        customerId,
        subjectRef: subjectRef || undefined,
        variables,
        signerName: signerName.trim(),
        checkedItems: buildCheckedItems(),
        answers: buildAnswers(),
        signatureImage
      })
      const signatureId = res?.data?.data?.signatureId
      message.success('已完成簽署')
      advanceOrFinish(template._id, 'signed', signatureId)
    } catch (err) {
      message.error(getApiErrorMessage(err, '簽署失敗'))
    } finally {
      setSubmitting(false)
    }
  }

  const handleSendRemote = async () => {
    if (!template) return
    setSubmitting(true)
    try {
      await sendPurchaseConsent(businessId, {
        templateId: template._id,
        target,
        customerId,
        subjectRef: subjectRef || undefined,
        variables
      })
      message.success('已傳送給客人，請客人於 App／網頁完成簽署')
      advanceOrFinish(template._id, 'sent', null)
    } catch (err) {
      message.error(getApiErrorMessage(err, '發送失敗'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      open={open}
      title={reminderMode ? '購買同意書提醒' : '購買同意書簽署'}
      onCancel={onClose}
      width={640}
      footer={[
        // 略過：購買前＝略過並完成購買；提醒模式＝關閉
        <Button key='skip' loading={submitting} onClick={handleSkip}>
          {reminderMode ? '關閉' : '略過（稍後再簽）'}
        </Button>,
        mode === 'onsite' ? (
          <Button
            key='sign'
            type='primary'
            loading={submitting}
            disabled={!template}
            onClick={handleSignOnsite}
            icon={<EditOutlined />}
          >
            確認簽署
            {templates.length > 1 && `（${activeIdx + 1}/${templates.length}）`}
          </Button>
        ) : (
          <Button
            key='send'
            type='primary'
            loading={submitting}
            disabled={!template}
            onClick={handleSendRemote}
            icon={<SendOutlined />}
          >
            傳送給客人
            {templates.length > 1 && `（${activeIdx + 1}/${templates.length}）`}
          </Button>
        )
      ]}
    >
      {loading ? (
        <Spin />
      ) : !template ? (
        <Empty description='此購買未綁定購買同意書' />
      ) : (
        <>
          {autoSent && (
            <BrandHint
              tone='success'
              title='同意書已自動傳送給顧客'
              style={{ marginBottom: 12 }}
            >
              系統已將此同意書發送至顧客的 App／LINE／Email，顧客可自行簽署；也可請顧客在此現場簽署。
            </BrandHint>
          )}
          {!autoSent && (
            <Segmented
              block
              value={mode}
              onChange={setMode}
              options={[
                { label: '現場簽署', value: 'onsite' },
                { label: '傳送給客人簽', value: 'remote' }
              ]}
              style={{ marginBottom: 12 }}
            />
          )}

          {templates.length > 1 && (
            <>
              <BrandHint tone='info' style={{ marginBottom: 10 }}>
                這筆購買有 {templates.length} 份適用的同意書，請逐份完成；
                打勾的是這次已經處理好的。
              </BrandHint>
              <Segmented
                block
                value={String(activeIdx)}
                onChange={(v) => {
                  setActiveIdx(Number(v))
                  setChecked({})
                  setAnswers({})
                  clearCanvas()
                }}
                options={templates.map((t, i) => ({
                  value: String(i),
                  label: `${doneMap[t._id] ? '✓ ' : ''}${t.title}`
                }))}
                style={{ marginBottom: 12 }}
              />
            </>
          )}

          <Text strong>{template.title}</Text>
          <ContentBox style={{ marginTop: 8 }}>
            {render(template.content, variables)}
          </ContentBox>

          {mode === 'onsite' ? (
            <>
              {(template.checkboxes || []).length > 0 && (
                <>
                  <Divider style={{ margin: '14px 0' }} />
                  <Space direction='vertical' size={8} style={{ width: '100%' }}>
                    {template.checkboxes.map((item) => (
                      <Checkbox
                        key={item.key}
                        checked={!!checked[item.key]}
                        onChange={(e) =>
                          setChecked((p) => ({ ...p, [item.key]: e.target.checked }))
                        }
                      >
                        {item.label}
                        {item.required && <Text type='danger'> *</Text>}
                      </Checkbox>
                    ))}
                  </Space>
                </>
              )}
              {(template.questions || []).length > 0 && (
                <>
                  <Divider style={{ margin: '14px 0' }} />
                  <Space direction='vertical' size={16} style={{ width: '100%' }}>
                    {template.questions.map((q, idx) => {
                      const a = answers[q.key] || {}
                      const setAnswer = (patch) =>
                        setAnswers((prev) => ({
                          ...prev,
                          [q.key]: { ...(prev[q.key] || {}), ...patch }
                        }))
                      return (
                        <div key={q.key}>
                          <div style={{ marginBottom: 6, fontWeight: 600 }}>
                            {idx + 1}. {q.label}
                            {q.required && <Text type='danger'> *</Text>}
                          </div>
                          {q.type === 'text' && (
                            <Input.TextArea
                              rows={2}
                              maxLength={500}
                              value={a.textValue || ''}
                              placeholder={q.placeholder || '請填寫'}
                              onChange={(e) =>
                                setAnswer({ textValue: e.target.value })
                              }
                            />
                          )}
                          {q.type === 'single' && (
                            <Radio.Group
                              value={(a.selectedKeys || [])[0]}
                              onChange={(e) =>
                                setAnswer({ selectedKeys: [e.target.value] })
                              }
                            >
                              <Space direction='vertical' size={6}>
                                {(q.options || []).map((o) => (
                                  <Radio key={o.key} value={o.key}>
                                    {o.label}
                                  </Radio>
                                ))}
                              </Space>
                            </Radio.Group>
                          )}
                          {q.type === 'multiple' && (
                            <Checkbox.Group
                              value={a.selectedKeys || []}
                              onChange={(vals) => setAnswer({ selectedKeys: vals })}
                            >
                              <Space direction='vertical' size={6}>
                                {(q.options || []).map((o) => (
                                  <Checkbox key={o.key} value={o.key}>
                                    {o.label}
                                  </Checkbox>
                                ))}
                              </Space>
                            </Checkbox.Group>
                          )}
                        </div>
                      )
                    })}
                  </Space>
                </>
              )}
              <Divider style={{ margin: '14px 0' }} />
              <div style={{ marginBottom: 10 }}>
                <Text>簽署人姓名 <Text type='danger'>*</Text></Text>
                <Input
                  value={signerName}
                  onChange={(e) => setSignerName(e.target.value)}
                  placeholder='請輸入姓名'
                  style={{ marginTop: 4 }}
                />
              </div>
              {template.requireSignature && (
                <SignSection>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <Text>手寫簽名 <Text type='danger'>*</Text></Text>
                    <Button
                      size='small'
                      type='text'
                      icon={<ClearOutlined />}
                      onClick={clearCanvas}
                    >
                      清除
                    </Button>
                  </div>
                  <CanvasWrap>
                    <StyledCanvas
                      ref={canvasRef}
                      onMouseDown={startDraw}
                      onMouseMove={moveDraw}
                      onMouseUp={endDraw}
                      onMouseLeave={endDraw}
                      onTouchStart={startDraw}
                      onTouchMove={moveDraw}
                      onTouchEnd={endDraw}
                      onTouchCancel={endDraw}
                    />
                    {showHint && <Hint>請客人在此區域簽名</Hint>}
                  </CanvasWrap>
                </SignSection>
              )}
            </>
          ) : (
            <Paragraph type='secondary' style={{ marginTop: 12 }}>
              將把此同意書傳送到客人的 App／網頁「我的同意書」，由客人自行閱讀並簽署。
              {template.blockUntilSigned
                ? '此範本設定為「未簽不得購買」，客人簽署完成後才能完成本次購買。'
                : '你可以先完成購買，客人稍後再簽（此範本未設定強制簽署）。'}
            </Paragraph>
          )}
        </>
      )}
    </Modal>
  )
}

export default PurchaseConsentModal
